import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepIcon from '@mui/material/StepIcon';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CancelIcon from '@mui/icons-material/Cancel';
import { Link as RouterLink } from 'react-router-dom';
import { STEP_CONFIG, deriveActiveStep } from '../../patient-details/hooks/usePatientProgress';


function PipelineStepIcon(props) {
    if (props.error) {
        return (
            <CancelIcon
                color="error"
                sx={{ width: 18, height: 18, transform: 'scale(1.25)' }}
            />
        );
    }
    return <StepIcon {...props} />;
}

/**
 * Live pipeline progress for a patient with no viewable/actionable output yet
 * (before segmentation, during segmentation, or post-validation while the 3D
 * model is being built). Renders in place of the MRI/3D viewer panels
 *
 * @param {{ currentStepKey: string, percentage: number, currentStepLabel: string, isError?: boolean }} props
 */
export default function PatientProcessingStepper({ currentStepKey, percentage, currentStepLabel, isError = false }) {
    const activeStep = Math.max(0, deriveActiveStep(currentStepKey));

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Paper
                variant="outlined"
                role="status"
                aria-live="polite"
                sx={{
                    width: { xs: 'min(94%, 640px)', md: 'min(80%, 820px)', xl: 'min(72%, 1500px)' },
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 3, md: 4, xl: 'clamp(48px, 24px + 1.56vw, 64px)' },
                    px: { xs: 2.5, sm: 4, md: 6, xl: 'clamp(80px, 44px + 2.34vw, 104px)' },
                    py: { xs: 3, sm: 4, md: 5, xl: 'clamp(64px, 34px + 1.95vw, 84px)' },
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: '1.25rem',
                    }}
                >
                    {currentStepLabel || 'Processing...'}
                </Typography>

                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{
                        width: '100%',
                        '& .MuiStepIcon-root': { width: 18, height: 18 },
                        '& .MuiStepLabel-label': { fontSize: '0.85rem', mt: 0.75 },
                        '& .MuiStepLabel-label.Mui-error': { color: 'text.primary' },
                    }}
                >
                    {STEP_CONFIG.map((step, index) => (
                        <Step key={step.key}>
                            <StepLabel
                                error={isError && index === activeStep}
                                StepIconComponent={PipelineStepIcon}
                                optional={index === activeStep ? (
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{percentage}%</Typography>
                                ) : null}
                            >
                                {step.shortLabel}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Link component={RouterLink} to="/patients" sx={{ fontSize: '0.9rem' }}>
                    View all patients
                </Link>
            </Paper>
        </Box>
    );
}
